import React, {
    FC,
    useCallback,
    useEffect,
    useRef,
    useState,
    Fragment,
} from "react";
import {useParams} from "react-router-dom";
import {registerLocale} from "react-datepicker";
import {useSelector, useDispatch} from "react-redux";
import ru from "date-fns/locale/ru";
import moment from "moment";
import clsx from "clsx";

import VisitsByDaysChart from "../../../components/charts/visitsByDaysChart/visitsByDaysChart";
import MediumFacingChart from "../../../components/charts/mediumFacingChart/mediumFacingChart";
import CheckStatusChart from "../../../components/charts/checkStatus/checkStatusChart";
import ReportsChart from "../../../components/charts/reportsChart/reportsChart";
import ActionsChart from "../../../components/charts/actionsChart/actionsChart";
import RateStatus from "../../../components/charts/rateStatus/rateStatusChart";
import FacingChart from "../../../components/charts/facingCharts/facinCharts";
import DynamicsChart from "../../../components/charts/dynamics/dynamicsChart";
import OssChart from "../../../components/charts/ossChart/ossChart";

import DragManagementChart from "./dragManagementChart";

import {AppDispatch} from "../../../redux/reducer/store";
import {actionGetUserReportsFilter} from "../../../redux/action/userReports/actionUserReports";
import {
    actionTest,
    actionAddDesktop,
    actionAnalyticOos,
    actionsReportsChart,
    actionAnalyticVisit,
    actionsAnalyticFacing,
    actionAnalyticFilters,
    actionsAnalyticActions,
    actionAnalyticSkuAverage,
    actionsAnalyticStatusCity,
    actionsAnalyticRateStatus,
    actionsAnalyticCheckStatus,
    actionsSignalChart,
} from "../../../redux/action/charts/actionCharts";
import {
    reducerAddData,
    reducerNewCharts,
    reducerSelectCharts,
    reducerPendingCharts,
    reducerAddChartsList,
    reducerChartsInputValues,
    reducerEditDesktopInputValues,
} from "../../../redux/reducer/charts/reducer/reducerCharts";

import Loader from "../../../components/custom/loader/loader";
import CustomMultiSelect from "../../../components/custom/customMultiSelect/customMultiSelect";
import RangeDatePicker from "../../../components/custom/rangeDatePicker/rangeDatePicker";

import {addMutedColors, defaultFilter} from "../../../utils/chartsData";
import {IUserOptions} from "../../../types/interfaces";

import styles from "../charts.module.scss";
import TanderSignal from "../../../components/charts/tanderSignal/tanderSignal";

interface ChartItem {
    el: JSX.Element;
    id: number;
    type?: string;
    isCopy?: boolean;
}

const defaultOptions = [
    {value: "project", label: "Проект"},
    {value: "contracts", label: "Контракты"},
    {value: "region", label: "Регион"},
    {value: "areas", label: "Область"},
    {value: "сhains", label: "Сеть"},
    {value: "date", label: "Период"},
];

const CustomerCharts: FC<{ data: any }> = ({data}) => {
    const {id} = useParams<{ id: string }>();
    const dispatch: any = useDispatch<AppDispatch>();

    const fieldRef: React.RefObject<HTMLDivElement> = useRef(null);
    const {isOpenSidebar} = useSelector(
        (state: IUserOptions) => state.user.user
    );
    const {
        desktopList,
        selectChart,
        listSetting,
        reorderedOptions,
        globalChainOptions,
        desktopInputValues,
    } = useSelector((state: any) => state.charts?.chartsList);

    const {areasOptions, regionsOptions, projectOptions, contractsOptions} =
        useSelector((state: any) => state.userReports.userReports);

    const [charts, setCharts] = useState<ChartItem[]>([]);
    const [isPending, setIsPending] = useState<boolean>(false);
    const [isDrag, setIsDrag] = useState<boolean>(false);

    const inputValues =
        desktopInputValues?.[Number(id)]?.generalFilters || defaultFilter;

    const chartsListSettings = listSetting;
    const activeFilters =
        chartsListSettings && chartsListSettings.length > 0
        ? chartsListSettings
        : defaultOptions.map((option) => option.value);

    const orderListSettings = Array.isArray(reorderedOptions)
                              ? reorderedOptions
                              : defaultOptions;

    const createDate = {
        formattedDateFrom: moment(inputValues?.fromDate, "YYYY-MM-DD").format(
            "DD/MM/YYYY"
        ),
        formattedDateTo: moment(inputValues?.toDate, "YYYY-MM-DD").format(
            "DD/MM/YYYY"
        ),
    };

    const removeChart = (idChart: number) => {
        if (data) {
            setIsDrag(false);
            const removedItem = data.find((item: any) => item.id === idChart);
            const newDate = data.filter((item: any) =>
                                            removedItem.isCopy
                                            ? item.id !== idChart
                                            : item.type !== removedItem.type
            );
            setCharts((prev: any) => prev.filter((item: any) => item.id !== idChart));
            dispatch(reducerAddData({id, charts: newDate}));
            dispatch(reducerSelectCharts(null));
            const charts = {
                chartsList: newDate.map((el: ChartItem) => {
                    return el.type;
                }),
            };
            dispatch(reducerNewCharts({id: id ? id : "0", charts}));
            dispatch(actionAddDesktop());
            const newDesktopInputValues = {...desktopInputValues[id || "0"]};
            delete newDesktopInputValues[idChart];
            dispatch(
                reducerEditDesktopInputValues({
                                                  ...desktopInputValues,
                                                  [id || "0"]: newDesktopInputValues,
                                              })
            );
        }
    };

    const copyChart = (idCopy: number) => {
        const chartToDuplicate = data.find((item: any) => item.id === idCopy);

        if (chartToDuplicate) {
            const newId = Math.max(...data.map((item: any) => item.id), 0) + 1;
            const index = id
                          ? desktopList.findIndex((el: any) => el.id.toString() === id)
                          : 0;
            setIsDrag(false);
            dispatch(
                reducerAddData({
                                   id,
                                   charts: [
                                       ...desktopList[index].list,
                                       {
                                           id: newId,
                                           type: chartToDuplicate.type,
                                           isCopy: true,
                                       },
                                   ],
                               })
            );
            dispatch(actionAddDesktop());
        }
    };

    useEffect(() => {
        getFilters();
        // eslint-disable-next-line
    }, [id]);

    useEffect(() => {
        setIsPending(true);
        dispatch(actionGetUserReportsFilter()).then(() => {
            setIsPending(false);
        });
        // eslint-disable-next-line
    }, []);

    const getFilters = () => {
        setIsPending(true);
        dispatch(actionAnalyticFilters(id || "0", ["chain"], false)).then(() => {
            setIsPending(false);
        });
    };

    const moveHorizontalChart = useCallback(
        (dragIndex: number, hoverIndex: number) => {
            const newChart = [...charts];
            const dragChart = newChart[dragIndex];
            newChart.splice(dragIndex, 1);
            newChart.splice(hoverIndex, 0, dragChart);
            const chartIds = newChart.map((chart) => ({
                id: chart.id,
                type: chart.type,
                isCopy: chart.el.props.isCopy || false,
            }));
            setIsDrag(true);
            setCharts(newChart);
            dispatch(reducerAddData({id, charts: chartIds}));
        },
        // eslint-disable-next-line
        [charts]
    );

    const moveVerticallyChart = useCallback(
        (dragIndex: number, hoverIndex: number) => {
            const newChart = [...charts];
            newChart[dragIndex] = charts[hoverIndex];
            newChart[hoverIndex] = charts[dragIndex];
            setIsDrag(true);
            setCharts(newChart);
            dispatch(reducerAddData({id, charts: newChart}));
        },
        // eslint-disable-next-line
        [charts]
    );

    const addPending = (chartId: number, action: any) => {
        dispatch(reducerPendingCharts({id, selectId: chartId}));
        return dispatch(action(chartId, id)).then(() => {
            dispatch(reducerPendingCharts({id, selectId: chartId}));
        });
    };

    const handleChangeInputValues = (el?: boolean) => {
        if (el) {
            getFilters();
        }
        const fetchAnalyticsData = async () => {
            const findItem = desktopList.find((el: any) => el.id.toString() === id);
            const elem = findItem ? findItem : desktopList[0];
            if (elem && elem.list.length > 0) {
                setIsPending(true);
                const promises = elem.list.map((data: ChartItem) => {
                    if (data.type === "visit") {
                        addPending(data.id, actionAnalyticVisit);
                    } else if (data.type === "oss") {
                        addPending(data.id, actionAnalyticOos);
                    } else if (data.type === "mediumFace") {
                        addPending(data.id, actionAnalyticSkuAverage);
                    } else if (data.type === "actions") {
                        addPending(data.id, actionsAnalyticActions);
                    } else if (data.type === "rateStatus") {
                        addPending(data.id, actionsAnalyticRateStatus);
                    } else if (data.type === "checkStatus") {
                        addPending(data.id, actionsAnalyticCheckStatus);
                    } else if (data.type === "facing") {
                        addPending(data.id, actionsAnalyticFacing);
                    } else if (data.type === "dynamics") {
                        addPending(data.id, actionsAnalyticStatusCity);
                    } else if (data.type === "reportsChart") {
                        addPending(data.id, actionsReportsChart);
                    } else if (data.type === "tanderSignal") {
                        addPending(data.id, actionsSignalChart);
                    }
                    return Promise.resolve();
                });

                await Promise.all(promises);
                setIsPending(false);
            }
        };
        fetchAnalyticsData();
    };

    useEffect(() => {
        registerLocale("ru", ru);
    }, []);

    useEffect(() => {
        if (id && desktopList && desktopList.length > 0) {
            if (!isDrag) {
                const newList: any = [];

                const findItem = desktopList.find((el: any) => el.id.toString() === id);
                const elem = findItem ? findItem : desktopList[0];

                elem?.list.forEach(
                    (
                        chart: { id: number; type: string; isCopy: boolean },
                        ind: number
                    ) => {
                        let newItem: any;
                        const index = chart.id;

                        if (chart.type === "visit") {
                            dispatch(
                                reducerAddChartsList({id, chartId: index, type: "visit"})
                            );
                            newItem = {
                                el: (
                                    <VisitsByDaysChart
                                        id={index}
                                        copyChart={copyChart}
                                        isCopy={chart.isCopy}
                                        pageId={id}
                                    />
                                ),
                                id: index,
                                type: "visit",
                            };
                        } else if (chart.type === "oss") {
                            dispatch(
                                reducerAddChartsList({id, chartId: index, type: "oss"})
                            );
                            newItem = {
                                el: (
                                    <OssChart
                                        id={index}
                                        copyChart={copyChart}
                                        isCopy={chart.isCopy}
                                        pageId={id}
                                    />
                                ),
                                id: index,
                                type: "oss",
                            };
                        } else if (chart.type === "actions") {
                            dispatch(
                                reducerAddChartsList({id, chartId: index, type: "actions"})
                            );
                            newItem = {
                                el: (
                                    <ActionsChart
                                        id={index}
                                        copyChart={copyChart}
                                        isCopy={chart.isCopy}
                                        pageId={id}
                                    />
                                ),
                                id: index,
                                type: "actions",
                            };
                        } else if (chart.type === "rateStatus") {
                            dispatch(
                                reducerAddChartsList({id, chartId: index, type: "rateStatus"})
                            );
                            newItem = {
                                el: (
                                    <RateStatus
                                        id={index}
                                        copyChart={copyChart}
                                        isCopy={chart.isCopy}
                                        pageId={id}
                                    />
                                ),
                                id: index,
                                type: "rateStatus",
                            };
                        } else if (chart.type === "checkStatus") {
                            dispatch(
                                reducerAddChartsList({
                                                         id,
                                                         chartId: index,
                                                         type: "checkStatus",
                                                     })
                            );
                            newItem = {
                                el: (
                                    <CheckStatusChart
                                        id={index}
                                        copyChart={copyChart}
                                        isCopy={chart.isCopy}
                                        pageId={id}
                                    />
                                ),
                                id: index,
                                type: "checkStatus",
                            };
                        } else if (chart.type === "facing") {
                            dispatch(
                                reducerAddChartsList({id, chartId: index, type: "facing"})
                            );
                            newItem = {
                                el: (
                                    <FacingChart
                                        id={index}
                                        copyChart={copyChart}
                                        isCopy={chart.isCopy}
                                        pageId={id}
                                    />
                                ),
                                id: index,
                                type: "facing",
                            };
                        } else if (chart.type === "dynamics") {
                            dispatch(
                                reducerAddChartsList({id, chartId: index, type: "dynamics"})
                            );
                            newItem = {
                                el: (
                                    <DynamicsChart
                                        id={index}
                                        copyChart={copyChart}
                                        isCopy={chart.isCopy}
                                        pageId={id}
                                    />
                                ),
                                id: index,
                                type: "dynamics",
                            };
                        } else if (chart.type === "reportsChart") {
                            dispatch(
                                reducerAddChartsList({
                                                         id,
                                                         chartId: index,
                                                         type: "reportsChart",
                                                     })
                            );
                            newItem = {
                                el: (
                                    <ReportsChart
                                        id={index}
                                        copyChart={copyChart}
                                        isCopy={chart.isCopy}
                                        pageId={id}
                                    />
                                ),
                                id: index,
                                type: "reportsChart",
                            };
                        } else if (chart.type === "tanderSignal") {
                            dispatch(
                                reducerAddChartsList({
                                                         id,
                                                         chartId: index,
                                                         type: "tanderSignal",
                                                     })
                            );
                            newItem = {
                                el: (
                                    <TanderSignal
                                        id={index}
                                        copyChart={copyChart}
                                        isCopy={chart.isCopy}
                                        pageId={id}
                                    />
                                ),
                                id: index,
                                type: "tanderSignal",
                            };
                        } else {
                            dispatch(
                                reducerAddChartsList({id, chartId: index, type: "sku"})
                            );
                            newItem = {
                                el: (
                                    <MediumFacingChart
                                        id={index}
                                        copyChart={copyChart}
                                        isCopy={chart.isCopy}
                                        pageId={id || "0"}
                                    />
                                ),
                                id: index,
                                type: "mediumFace",
                            };
                        }

                        newList.push(newItem);
                    }
                );

                setCharts(newList);
            }
        } else {
            setCharts([]);
        }
        // eslint-disable-next-line
    }, [desktopList, id]);

    useEffect(() => {
        if (selectChart || selectChart === 0) {
            removeChart(selectChart);
        }
        // eslint-disable-next-line
    }, [selectChart]);

    useEffect(() => {
        addMutedColors();
        // eslint-disable-next-line
    }, []);

    return (
        <>
            <div className="loader__wrapper">{isPending && <Loader/>}</div>
            <div
                className={clsx(
                    styles.charts,
                    isOpenSidebar ? styles.charts_active : styles.charts_close
                )}
                ref={fieldRef}
            >
                <div
                    className={clsx(
                        styles.container,
                        chartsListSettings &&
                        chartsListSettings?.length === 0 &&
                        styles.container_long
                    )}
                >
                    <div className={styles.charts__info}>
                        <div
                            className={clsx(
                                styles.charts__filters,
                                isOpenSidebar && styles.charts__filters_opened
                            )}
                        >
                            {orderListSettings.map((el: { value: string }, index: number) => {
                                if (el.value === "date" && activeFilters.includes("date")) {
                                    return (
                                        <Fragment key={index}>
                                            <div
                                                className={clsx(
                                                    styles.charts__filters__mobile,
                                                    styles.charts__filters__mobile__full,
                                                    "filter__data_picker_container"
                                                )}
                                            >
                                                <div className="filter__data_picker">
                                                    <RangeDatePicker
                                                        setLoading={handleChangeInputValues}
                                                        reducer={reducerChartsInputValues}
                                                        defaultDateFrom={createDate.formattedDateFrom}
                                                        inputValues={inputValues}
                                                        defaultDateTo={createDate.formattedDateTo}
                                                        setCurrentPage={() => {
                                                        }}
                                                        action={actionTest}
                                                        setStartPage={() => {
                                                        }}
                                                        limit={10}
                                                        page={1}
                                                        placeholder="Период"
                                                        pageName="charts"
                                                        name="date"
                                                        id={id || "0"}
                                                    />
                                                </div>
                                            </div>
                                        </Fragment>
                                    );
                                }
                                if (
                                    el.value === "project" &&
                                    activeFilters.includes("project")
                                ) {
                                    return (
                                        <Fragment key={index}>
                                            <div
                                                className={clsx(
                                                    styles.charts__filters__mobile,
                                                    "multiselect__filter_block"
                                                )}
                                            >
                                                <CustomMultiSelect
                                                    setLoading={() => {
                                                    }}
                                                    inputValues={inputValues}
                                                    options={projectOptions}
                                                    isShowPlaceholder={true}
                                                    pageName="charts"
                                                    placeholder="Проект"
                                                    name="projects"
                                                    limit={10}
                                                    reducer={reducerChartsInputValues}
                                                    resetPages={handleChangeInputValues}
                                                    id={id || "0"}
                                                    activeOptions={inputValues.projects}
                                                />
                                            </div>
                                        </Fragment>
                                    );
                                }
                                if (
                                    el.value === "contracts" &&
                                    activeFilters.includes("contracts")
                                ) {
                                    return (
                                        <Fragment key={index}>
                                            <div
                                                className={clsx(
                                                    styles.charts__filters__mobile,
                                                    "multiselect__filter_block"
                                                )}
                                            >
                                                <CustomMultiSelect
                                                    setLoading={() => {
                                                    }}
                                                    inputValues={inputValues}
                                                    options={contractsOptions}
                                                    isShowPlaceholder={true}
                                                    pageName="charts"
                                                    placeholder="Контракт"
                                                    name="contracts"
                                                    limit={10}
                                                    reducer={reducerChartsInputValues}
                                                    resetPages={handleChangeInputValues}
                                                    id={id || "0"}
                                                    activeOptions={inputValues.contracts}
                                                />
                                            </div>
                                        </Fragment>
                                    );
                                }
                                if (el.value === "region" && activeFilters.includes("region")) {
                                    return (
                                        <Fragment key={index}>
                                            <div
                                                className={clsx(
                                                    styles.charts__filters__mobile,
                                                    "multiselect__filter_block"
                                                )}
                                            >
                                                <CustomMultiSelect
                                                    setLoading={() => {}}
                                                    inputValues={inputValues}
                                                    options={regionsOptions}
                                                    isShowPlaceholder={true}
                                                    pageName="charts"
                                                    placeholder="Регион"
                                                    name="regions"
                                                    limit={10}
                                                    reducer={reducerChartsInputValues}
                                                    resetPages={handleChangeInputValues}
                                                    id={id || "0"}
                                                    activeOptions={inputValues.regions}
                                                />
                                            </div>
                                        </Fragment>
                                    );
                                }
                                if (el.value === "areas" && activeFilters.includes("areas")) {
                                    return (
                                        <Fragment key={index}>
                                            <div
                                                className={clsx(
                                                    styles.charts__filters__mobile,
                                                    "multiselect__filter_block"
                                                )}
                                            >
                                                <CustomMultiSelect
                                                    setLoading={() => {}}
                                                    inputValues={inputValues}
                                                    options={areasOptions}
                                                    isShowPlaceholder={true}
                                                    pageName="charts"
                                                    placeholder="Область"
                                                    name="areas"
                                                    limit={10}
                                                    reducer={reducerChartsInputValues}
                                                    resetPages={handleChangeInputValues}
                                                    id={id || "0"}
                                                    activeOptions={inputValues.areas}
                                                />
                                            </div>
                                        </Fragment>
                                    );
                                }
                                if (el.value === "сhains" && activeFilters.includes("сhains")) {
                                    return (
                                        <Fragment key={index}>
                                            <div
                                                className={clsx(
                                                    styles.charts__filters__mobile,
                                                    styles.charts__filters__mobile__full,
                                                    "multiselect__filter_block"
                                                )}
                                            >
                                                <CustomMultiSelect
                                                    setLoading={() => {}}
                                                    inputValues={inputValues}
                                                    options={globalChainOptions}
                                                    isShowPlaceholder={true}
                                                    pageName="charts"
                                                    placeholder="Сеть"
                                                    name="retailChains"
                                                    limit={10}
                                                    reducer={reducerChartsInputValues}
                                                    resetPages={handleChangeInputValues}
                                                    id={id || "0"}
                                                    activeOptions={inputValues.retailChains}
                                                />
                                            </div>
                                        </Fragment>
                                    );
                                }
                                return null;
                            })}
                        </div>
                    </div>
                    <div className={styles.charts__grid}>
                        {charts.length > 0 &&
                         charts.map((card: { el: any; id: number }, index: number) => (
                             <DragManagementChart
                                 key={card.id}
                                 index={index}
                                 id={card.id}
                                 moveHorizontalChart={moveHorizontalChart}
                                 moveVerticallyChart={moveVerticallyChart}
                                 el={charts[index] && charts[index].el}
                             />
                         ))}
                    </div>
                </div>
            </div>
        </>
    );
};

export default CustomerCharts;
